/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { GetStaticPaths, GetStaticProps, NextLayoutComponentType } from 'next';

import { Api } from '@/api';
import MainLayout from '@/layouts/main';
import { routes } from '@/infrastructure/const/routes';
import { Reciter } from '@/infrastructure/class/reciter';
import AppContainer from '@/components/layout/container';
import { localeList } from '@/public/locales/locale-list';
import { replaceWords } from '@/utils/helpers/replace-words';
import AudioSurahsList from '@/components/audio-surahs/list';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import SeoHead from '@/components/common/seo-head';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import ReciterDetailHeader from '@/components/reciters/detail/header';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import ReciterDescription from '@/components/reciters/detail/description';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import RecitersFeaturedCarousel from '@/components/reciters/featured-carousel';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  collectionPageMarkupSchema,
  organizationMarkupSchema,
  personMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';
import s from './styles.module.scss';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

const isValidSlug = (slug: unknown): slug is string =>
  typeof slug === 'string' && slug.trim().length > 0;

interface IReciterDetailPageProps {
  reciter: IReciter;
  reciters: IReciter[];
  audioSurahs: IAudioSurah[];
  serverPage?: number;
}

const ReciterDetailPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IReciterDetailPageProps>(data);
  const { reciter, reciters, audioSurahs, serverPage } = decrypted;

  const router = useRouter();
  const { locale } = router;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tLinks } = useTranslation('links');
  const { t: tMeta } = useTranslation('meta');
  const { t: tCommon } = useTranslation('common');

  const [reciterDetail, setReciterDetail] = useState<Reciter>(
    new Reciter(reciter, locale)
  );

  const reciterDescriptionRef = useRef<HTMLDivElement | null>(null);

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('allReciters'),
      path: routes.reciters,
    },
    {
      title: reciterDetail.titleEn,
      path: `${routes.reciters}/${reciterDetail.slug}`,
    },
  ];

  const canonicalURL =
    `${SITE_URL}${getLocaleForUrl(locale)}` +
    `${routes.reciters}/${reciterDetail.slug}`;

  const htmlCanonical = (): string => {
    return `${canonicalURL}${
      Number(serverPage) > 1 ? `?page=${Number(serverPage)}` : ''
    }`;
  };
  const htmlTitle = (): string => {
    if (reciterDetail.metaTitle) return reciterDetail.metaTitle;

    return `${replaceWords(`${tMeta('reciterDetailPageTitle')}`, {
      title: reciterDetail.titleEn,
    })} ${
      Number(serverPage) > 1 ? `- ${tCommon('page')} ${Number(serverPage)}` : ''
    }`;
  };
  const htmlDescription = (): string => {
    if (reciterDetail.metaDescription) return reciterDetail.metaDescription;

    return `${replaceWords(`${tMeta('reciterDetailPageDescription')}`, {
      title: reciterDetail.titleEn,
    })} ${
      Number(serverPage) > 1 ? `- ${tCommon('page')} ${Number(serverPage)}` : ''
    }`;
  };

  const shema = baseMarkupSchema([
    webPageMarkupSchema(
      `${routes.reciters}/${reciterDetail.slug}`,
      htmlTitle(),
      htmlDescription()
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    collectionPageMarkupSchema(reciterDetail),
    personMarkupSchema(reciterDetail),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  const handleScrollToDescription = () => {
    if (reciterDescriptionRef.current) {
      reciterDescriptionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setReciterDetail(new Reciter(reciter, locale));
  }, [data, locale]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <SeoHead
        title={htmlTitle()}
        description={htmlDescription()}
        canonicalURL={htmlCanonical()}
        schema={shema}
        image={reciterDetail.image?.originalUrl}
      />
      <main>
        <ReciterDetailHeader
          className={s['detail-header']}
          data={reciterDetail}
          audioSurahs={audioSurahs}
          serverPage={serverPage}
          onReadMoreClick={handleScrollToDescription}
        />
        <AppContainer>
          <AudioSurahsList
            items={audioSurahs}
            partial={reciterDetail.isPartial}
            href={`${routes.reciters}/${reciterDetail.slug}`}
            reciterSlug={reciterDetail.slug}
          />
        </AppContainer>
        <RecitersFeaturedCarousel
          className={s['featured-carousel']}
          title={tCommon('moreReciters')}
          items={reciters}
          secondType
          fullWidth
        />
        <AppContainer>
          <ReciterDescription
            ref={reciterDescriptionRef}
            data={reciterDetail}
          />
        </AppContainer>
      </main>
    </>
  );
};

ReciterDetailPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticPaths: GetStaticPaths = async () => {
  const { data: recitersResult } = await api.getReciters(
    allRecordsPaginationOptions,
    ['slug']
  );
  const { results: reciters } = recitersResult;

  return {
    paths: reciters
      .map((reciter) => reciter.slug)
      .filter(isValidSlug)
      .map((slug) => ({
        params: { slug },
      })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({
  locale = '',
  params,
}) => {
  try {
    const slug = params?.slug;

    const { data } = await api.getReciterBySlug(String(slug));
    const { reciter } = data;
    addDomainForReciterImages(reciter);

    const { data: recitersResult } = await api.getNextReciters(
      reciter.id,
      {
        limit: 15,
      },
      ['id', 'image', 'slug', 'title_en', 'title_ar', 'order'],
      true
    );

    const { results: reciters } = recitersResult;
    reciters.map((reciter) => {
      addDomainForReciterImages(reciter);
    });

    const { data: audioSurahsResult } = await api.getAudioSurahsByReciterSlug(
      String(slug),
      allRecordsPaginationOptions
    );

    const { results: audioSurahs } = audioSurahsResult;
    const dataToEncrypt = {
      reciter,
      reciters,
      audioSurahs,
      serverPage: 1,
    };
    const { encryptPageProps } =
      await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default ReciterDetailPage;
